import React, { Dispatch, useContext, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { formatCurrency } from '@/utils/currency';
import { RapidStorSettings, unitTypeDealInclusions } from '@/utils/rapidstor';

import store from '@/store';
import {
    ReservationData,
    selectReservationData,
    selectUnitData,
    setReservationData,
    setUnitData,
} from '@/store/bookingSlice';

import Inclusions from '@/components/descriptor/inclusion';
import DescriptorTag from '@/components/descriptor/tag';
import Coupon from '@/components/rapidstor/coupon';
import Option from '@/components/unit-option';

import {
    ANIMATION_SPEED,
    selectedCardDesktopVariants,
    selectedCardMobileVariants,
} from '@/features/rapidstor/animations';
import {
    descriptorDescriptions,
    descriptorTitle,
    formatDealCustomText,
    formatTimeframe,
    percentageDifference,
} from '@/features/rapidstor/helpers';
import { useAnimationContext } from '@/features/rapidstor/providers/AnimationProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import {
    DescriptorOptions,
    unitOptionsTransformer,
    UpgradeDescriptor,
} from '@/features/rapidstor/UnitOptions';

import baseClasses from '../styles.module.scss';

import CardButtons from '../CardButtons';

interface Props {
    deal: string | null;
    descriptor: Descriptor;
    descriptorOptions: UpgradeDescriptor[];
    unitTypeOptions: UnitType[];
    desktop: boolean;
    reservationToken: string | null;
    selectedID: string;
    loading: boolean;
    setLoading: Dispatch<React.SetStateAction<boolean>>;

    makeReservation: () => void;
    setSelectedID: Dispatch<React.SetStateAction<string>>;
}

export default function SelectedCard({
    deal,
    descriptor,
    descriptorOptions,
    unitTypeOptions,
    desktop,
    reservationToken,
    selectedID,
    loading,
    setLoading,

    makeReservation,
    setSelectedID,
}: Props) {
    const location = useLoaderData() as Location;
    const reservationData = useSelector(selectReservationData);
    const unitData = useSelector(selectUnitData);
    const useDescriptorOptions = DescriptorOptions(descriptor, unitData?.descriptors ?? [], location);
    const { t } = useTranslation();
    const { toggleContactCard } = useContext(JourneyContext);
    const { selectedCardState, setSelectedCardState } = useAnimationContext();

    const options = useDescriptorOptions.isDescriptorUpsell()
        ? descriptorOptions
        : unitTypeOptions;

    const unitType = useDescriptorOptions.getUnitTypeUpsells()[0];

    const classNames = [
        baseClasses.card,
        desktop === true ? baseClasses.desktop : baseClasses.mobile,
        deal && baseClasses.deal,
        baseClasses.primary,
    ].join(' ');

    const selectUpsellCard = () => {
        if (!unitData) {
            return null;
        }
        store.dispatch(
            setUnitData({
                location,
                unitType: unitType,
                descriptor,
                descriptors: unitData.descriptors,
            })
        );

        const reservationStoreData: ReservationData = {
            unitType: unitType,
            descriptor,
            deal: '',
            coupon: null,
        };

        if (reservationToken) {
            reservationStoreData.token = reservationToken;
        }

        store.dispatch(setReservationData(reservationStoreData));
    };

    const cardVariantsToUse = useMemo(() => {
        if (desktop === true) {
            return selectedCardDesktopVariants;
        } else {
            return selectedCardMobileVariants;
        }
    }, [desktop]);

    const cardInitialValueToUse = useMemo(() => {
        if (desktop === true) {
            return 'start';
        } else {
            return 'start';
        }
    }, [desktop]);

    const shouldShowCardButtons = useMemo(() => {
        if (desktop) {
            const useDescriptorOptions = DescriptorOptions(descriptor, unitData?.descriptors ?? [], location);
            return useDescriptorOptions.isDescriptorUpsell()
                ? descriptorOptions.length <= 0
                : unitTypeOptions.length <= 0;
        } else {
            return true;
        }
    }, [descriptor, descriptorOptions.length, desktop, location, unitTypeOptions.length, unitData?.descriptors]);

    const percentageDifferenceValue = useMemo(() => {
        const original = reservationData?.unitType.rates.rate ?? 0;
        const discounted = reservationData?.dealObject
            ? reservationData?.dealObject?.rates.discountedRate ?? 0
            : reservationData?.unitType.rates.rate ?? 0;

        return percentageDifference(original, discounted);
    }, [reservationData]);

    const textBelowPrice = useMemo(() => {
        if (deal) {
            if (
                reservationData?.dealObject?.customFormat.primary &&
                reservationData.dealObject.customFormat.primary !== ''
            ) {
                return (
                    <div
                        className={`${baseClasses.pricing_desc} ${baseClasses.customText}`}
                    >
                        <span>
                            {formatDealCustomText(
                                reservationData?.dealObject,
                                location,
                                reservationData.dealObject.customFormat.primary
                            )}
                        </span>
                        <span>
                            {formatDealCustomText(
                                reservationData?.dealObject,
                                location,
                                reservationData.dealObject.customFormat.secondary
                            )}
                        </span>
                    </div>
                );
            } else {
                return (
                    <div className={baseClasses.pricing_desc}>
                        <span>
                            {deal
                                ? `${t('unit_card:valued_at')} ${formatCurrency(
                                    reservationData?.unitType.rates.rate ?? 0,
                                    location
                                )}`
                                : formatTimeframe(location)}
                        </span>
                        {deal &&
                            (reservationData?.unitType.rates.discountedRate ?? 0) > 0 ? (
                            <span className={baseClasses.savings_badge}>
                                {percentageDifferenceValue}
                            </span>
                        ) : (
                            ''
                        )}
                    </div>
                );
            }
        }
    }, [deal, location, percentageDifferenceValue, reservationData, t]);

    useEffect(() => {
        if (desktop) {
            setSelectedCardState('show');
        } else {
            if (options.length >= 1) {
                setSelectedCardState('show');
            } else {
                setSelectedCardState('close');
            }
        }
    }, [desktop, options.length, setSelectedCardState]);

    return (
        <motion.div
            layout={true}
            variants={cardVariantsToUse}
            initial={cardInitialValueToUse}
            animate={selectedCardState}
            id={`${selectedCardState}`}
            transition={{
                duration: ANIMATION_SPEED,
            }}
            className={`${baseClasses.card_container}`}
        >
            <div className={`${classNames}`}>
                {reservationData?.dealObject && reservationData?.deal && (
                    <div className={`${baseClasses.deal_banner}`}>
                        <span>{reservationData?.dealObject?.title}</span>
                    </div>
                )}

                <div className={`${baseClasses.card_body}`}>
                    <div className={`${baseClasses.header}`}>
                        <div className={baseClasses.first}>
                            <span>
                                <label className={`${baseClasses.underline}`}>
                                    {descriptorTitle(descriptor, location)[1]}
                                </label>
                            </span>
                        </div>
                        {options.length <= 1 && (
                            <div className={baseClasses.last}>
                                <>
                                    <span>
                                        {formatCurrency(
                                            deal
                                                ? reservationData?.dealObject?.rates
                                                    .discountedRate ?? 0
                                                : reservationData?.unitType.rates.rate ??
                                                0,
                                            location
                                        )}
                                    </span>
                                    {textBelowPrice}
                                </>
                            </div>
                        )}
                    </div>

                    <Inclusions
                        inclusions={
                            deal && reservationData?.unitType
                                ? unitTypeDealInclusions(reservationData.unitType)
                                : descriptorDescriptions(descriptor, false)
                        }
                    />

                    {desktop && (
                        <DescriptorTag
                            tags={unitType.deal ? unitType.deal.tags : descriptor.tags}
                            secondary
                        />
                    )}

                    {options.length >= 1 && reservationData && (
                        <Option
                            option={reservationData.unitType}
                            location={location}
                            dealObject={reservationData.dealObject}
                            default
                            selected={selectedID === reservationData.unitType._id}
                            optionText={descriptor.jblOptionText}
                            setSelectedID={setSelectedID}
                            theme={`base`}
                        />
                    )}

                    <div className={baseClasses.mobile}>
                        <span className={baseClasses.customise_booking}>
                            {t('unit_card:customise_booking')}
                        </span>
                        {useDescriptorOptions.isDescriptorUpsell() ? (
                            <>
                                {descriptorOptions.map((option) => unitOptionsTransformer(option)[0] && (
                                    <Option
                                        key={`${option._id}_upsell_options_idx`}
                                        location={location}
                                        option={unitOptionsTransformer(option)[0]}
                                        dealObject={unitOptionsTransformer(option)[0].deal}
                                        descriptor={option}
                                        desktopOption={desktop}
                                        initialRate={deal ? unitType.rates.discountedRate : unitType.rates.rate}
                                        selected={
                                            selectedID ===
                                            unitOptionsTransformer(option)[0]._id
                                        }
                                        setSelectedID={setSelectedID}
                                        theme={`base`}
                                    />
                                ))}
                            </>
                        ) : (
                            <>
                                {unitTypeOptions.map((option) => (
                                    <Option
                                        key={`${option._id}_upsell_options_idx`}
                                        location={location}
                                        option={option}
                                        selected={selectedID === option._id}
                                        desktopOption={desktop}
                                        initialRate={deal ? unitType.rates.discountedRate : unitType.rates.rate}
                                        setSelectedID={setSelectedID}
                                        theme={`base`}
                                    />
                                ))}
                            </>
                        )}
                    </div>

                    {RapidStorSettings(location).showDealCoupons() &&
                        !desktop &&
                        options.length >= 1 && <Coupon type='primary' />}

                    {shouldShowCardButtons && (
                        <CardButtons
                            isLoading={loading}
                            cardState={selectedCardState}
                            toggleContactCard={toggleContactCard}
                            makeReservation={makeReservation}
                            selectUpsellCard={selectUpsellCard}
                            setIsLoading={setLoading}
                            desktop={desktop}
                            unitType={unitType}
                            deal={deal}
                            options={unitTypeOptions}
                        />
                    )}
                </div>
            </div>

            {location.rapidstor.useDealCoupons && desktop && <Coupon type='base' />}
        </motion.div>
    );
}
